import type { ComponentType } from "react";
import React from "react";
import r2wc from "@r2wc/react-to-web-component";
import ReactDOM from "react-dom/client";

// Define more specific types for react-to-webcomponent
type PropType = "string" | "number" | "boolean" | "function" | "json";
type PropTypesConfig = Partial<Record<string, PropType>>;

// Define a type for our enhanced prop information
export interface PropInfo {
  name: string;
  type: PropType;
  optional: boolean;
}

// Map to hold kebab to camel case conversions
type PropMapping = Record<string, string>;

// Type for a custom element with dynamic properties
type DynamicProps = Record<string, unknown>;

// Type for WebComponent prototype with attributeChangedCallback
interface WebComponentPrototype {
  attributeChangedCallback?: (
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) => void;
}

/**
 * List of CSS variables to apply from parent or root context
 */
export const CSS_VARIABLES = [
  "--company-color",
  "--fluid-background",
  "--fluid-foreground",
  "--fluid-card",
  "--fluid-card-foreground",
  "--fluid-popover",
  "--fluid-popover-foreground",
  "--fluid-primary",
  "--fluid-primary-foreground",
  "--fluid-secondary",
  "--fluid-secondary-foreground",
  "--fluid-muted",
  "--fluid-muted-foreground",
  "--fluid-accent",
  "--fluid-accent-foreground",
  "--fluid-destructive",
  "--fluid-destructive-foreground",
  "--fluid-border",
  "--fluid-input",
  "--fluid-ring",
  "--fluid-radius",
];

/**
 * Convert camelCase to kebab-case
 */
function camelToKebab(str: string): string {
  return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * Convert kebab-case to camelCase
 */
function kebabToCamel(str: string): string {
  return str.replace(/-([a-z0-9])/g, (g) => g[1]?.toUpperCase() ?? "");
}

/**
 * Determine the prop type based on name and default to string
 */
function getPropType(propName: string): PropType {
  // Boolean props typically start with "is", "has", "show", "hide", etc.
  if (
    /^(is|has|show|hide|enable|disable)/.test(propName) ||
    propName.toLowerCase().includes("visible") ||
    propName.toLowerCase().includes("enabled") ||
    propName.toLowerCase().includes("active")
  ) {
    return "boolean";
  }

  // Number props typically include words like "index", "count", "size", etc.
  if (
    /(^|[A-Z])([iI]ndex|[cC]ount|[sS]ize|[wW]idth|[hH]eight|[mM]argin|[pP]adding|[zZ]Index)($|[A-Z])/.test(
      propName,
    ) ||
    /[nN]umber$/.test(propName)
  ) {
    return "number";
  }

  // Function props typically end with callback-like names
  if (
    /^on[A-Z]/.test(propName) ||
    /[cC]allback$/.test(propName) ||
    /[hH]andler$/.test(propName)
  ) {
    return "function";
  }

  // JSON props typically have complex data
  if (
    /[dD]ata$/.test(propName) ||
    /[cC]onfig$/.test(propName) ||
    /[oO]ptions$/.test(propName) ||
    /[sS]ettings$/.test(propName) ||
    /[iI]tems$/.test(propName) ||
    /[pP]roducts$/.test(propName)
  ) {
    return "json";
  }

  // Default to string
  return "string";
}

/**
 * Convert string attribute value to the appropriate type
 */
function convertAttributeValue(
  value: string | null,
  type: PropType,
  attrName: string,
): unknown {
  if (value === null) return null;

  try {
    switch (type) {
      case "boolean":
        // Handle boolean attributes (presence means true, absence means false)
        // Also convert string values like "true"/"false"
        return (
          value === "" ||
          value === "true" ||
          value === "1" ||
          value === attrName
        );
      case "number":
        return Number(value);
      case "function":
        // For function attributes, we can't convert directly from string
        // Instead, return a function that executes the attribute value as code
        // or triggers a custom event for external handling
        return (...args: unknown[]) => {
          // Create and dispatch a custom event that can be listened to externally
          const event = new CustomEvent(`fluid-${kebabToCamel(attrName)}`, {
            detail: args,
            bubbles: true,
            composed: true,
          });
          return window.dispatchEvent(event);
        };
      case "json":
        // Try to parse as JSON
        return JSON.parse(value);
      case "string":
      default:
        return value;
    }
  } catch (e) {
    console.warn(`Error converting attribute value "${value}" to ${type}:`, e);
    return value; // Return original value on error
  }
}

/**
 * Creates a web component from a React component
 * @param Component The React component to convert
 * @param propsInput Either an array of prop names or an array of detailed prop info
 * @param tagName The tag name for the web component
 * @returns A web component class
 */
export function createWebComponent<P extends object>(
  Component: ComponentType<P>,
  propsInput: string[] | PropInfo[],
  tagName?: string,
): CustomElementConstructor {
  console.log(
    `Creating web component ${tagName ?? ""} with props:`,
    propsInput,
  );

  // Determine if we have detailed prop info or just names
  const hasDetailedProps =
    propsInput.length > 0 &&
    typeof propsInput[0] !== "string" &&
    typeof propsInput[0]?.name === "string";

  // Extract prop names from input
  const propNames = hasDetailedProps
    ? (propsInput as PropInfo[]).map((p) => p.name)
    : (propsInput as string[]);

  console.log(
    `Using ${hasDetailedProps ? "detailed prop info" : "prop names only"}`,
  );

  // Wrap the component with minimal wrapper
  const WrappedComponent: React.FC<P> = (props) => {
    // Simply render the component directly
    return React.createElement(Component, props);
  };

  // Create a mapping of kebab-case attributes to camelCase props
  const propMapping: PropMapping = {};
  const propTypes: Record<string, PropType> = {};
  const allAttributes: string[] = [];

  // For each prop, create both camelCase and kebab-case versions and determine types
  propNames.forEach((propName) => {
    const kebabProp = camelToKebab(propName);
    propMapping[kebabProp] = propName;
    allAttributes.push(kebabProp);

    // Get the prop type from detailed info or infer it
    let propType: PropType;
    if (hasDetailedProps) {
      // Find the matching prop info
      const propInfo = (propsInput as PropInfo[]).find(
        (p) => p.name === propName,
      );
      propType = propInfo?.type ?? "string";
    } else {
      // Determine the type based on name patterns
      propType = getPropType(propName);
    }

    propTypes[propName] = propType;

    // Also include the original camelCase name for when used directly
    if (kebabProp !== propName) {
      propMapping[propName] = propName;
      allAttributes.push(propName);
    }
  });

  console.log("Prop mapping:", propMapping);
  console.log("Prop types:", propTypes);
  console.log("Observing attributes:", allAttributes);

  // Prepare the props configuration for r2wc
  const propsConfig: PropTypesConfig = {};
  propNames.forEach((prop) => {
    // Get the prop type or use string as a fallback
    propsConfig[prop] = propTypes[prop] ?? "string";
  });

  // Create the web component with r2wc
  try {
    // @r2wc/react-to-web-component automatically uses React and ReactDOM
    const WebComponentClass = r2wc(WrappedComponent, {
      props: propsConfig,
      shadow: undefined,
    });

    // Extend with our customizations
    class FluidWebComponent extends WebComponentClass {
      static get observedAttributes(): string[] {
        return allAttributes;
      }

      private _initialized = false;

      attributeChangedCallback(
        name: string,
        oldValue: string | null,
        newValue: string | null,
      ): void {
        // Only process if this name is in our mapping
        if (propMapping[name]) {
          const propName = propMapping[name];
          console.log(
            `Attribute changed: ${name} -> prop: ${propName}, value: ${newValue}`,
          );

          // Get the prop type and convert the value
          const propType = propTypes[propName] ?? "string";
          const convertedValue = convertAttributeValue(
            newValue,
            propType,
            name,
          );

          console.log(
            `Converted attribute: ${name} -> prop: ${propName}, type: ${propType}, value:`,
            convertedValue,
          );

          // Set the correctly mapped prop safely
          (this as unknown as DynamicProps)[propName] = convertedValue;
        }

        // Call parent implementation if it exists
        try {
          const proto = Object.getPrototypeOf(
            Object.getPrototypeOf(this),
          ) as WebComponentPrototype;
          if (typeof proto.attributeChangedCallback === "function") {
            proto.attributeChangedCallback.call(this, name, oldValue, newValue);
          }
        } catch (e) {
          console.warn("Error calling parent attributeChangedCallback:", e);
        }
      }

      constructor() {
        super();
        // Only set up initial state and default values in constructor
        // Per Web Components spec, defer other work to connectedCallback
      }

      connectedCallback() {
        // Prevent multiple initializations
        if (this._initialized) {
          return;
        }
        this._initialized = true;

        // Now it's safe to inspect attributes and manipulate DOM
        // Mark as fluid widget
        this.setAttribute("data-fluid-widget", "true");

        // Check for hide-widget attribute and set visibility
        if (this.hasAttribute("hide-widget")) {
          // Apply display:none style to the custom element directly
          this.style.display = "none";
        }

        // Copy all attribute values to mapped props on initial load
        Array.from(this.attributes).forEach((attr) => {
          const name = attr.name;
          if (propMapping[name]) {
            const propName = propMapping[name];
            const propType = propTypes[propName] ?? "string";
            const value = attr.value;

            // Convert the value to the appropriate type
            const convertedValue = convertAttributeValue(value, propType, name);

            console.log(
              `Initial attribute mapping: ${name} -> ${propName} = ${value} (${propType})`,
              convertedValue,
            );

            (this as unknown as DynamicProps)[propName] = convertedValue;
          }
        });

        // Initialize styles using the centralized style system
        initializeComponentStyles(this);

        // Create the container for React only if shadow root exists
        if (this.shadowRoot) {
          const container = document.createElement("div");
          container.className = "react-container";
          this.shadowRoot.appendChild(container);
        }

        // Call parent connectedCallback if it exists
        try {
          const proto = Object.getPrototypeOf(Object.getPrototypeOf(this)) as {
            connectedCallback?: () => void;
          };
          if (typeof proto.connectedCallback === "function") {
            proto.connectedCallback.call(this);
          }
        } catch (e) {
          console.warn("Error calling parent connectedCallback:", e);
        }

        // Render React component
        try {
          const root = ReactDOM.createRoot(this);
          // @ts-expect-error - Dynamic props can't be fully typed here
          root.render(React.createElement(WrappedComponent, this.getProps()));
        } catch (error) {
          console.error("Error rendering React component:", error);
        }
      }

      // Add getProps method to collect all props from the element
      getProps(): Record<string, unknown> {
        const props: Record<string, unknown> = {};

        // Copy all props from the element to the props object
        for (const propName of propNames) {
          if (propName in this) {
            props[propName] = (this as unknown as DynamicProps)[propName];
          }
        }

        return props;
      }
    }

    return FluidWebComponent;
  } catch (error) {
    console.error("Error creating web component:", error);

    // Return a fallback component if r2wc fails
    class FallbackComponent extends HTMLElement {
      connectedCallback() {
        this.innerHTML = `<div class="error-message">Error creating component</div>`;
        console.error("Using fallback component due to error:", error);
      }
    }

    return FallbackComponent;
  }
}

// Add this function to create and initialize the web component with proper shadow DOM style support
export function initializeComponentStyles(element: HTMLElement): void {
  // Skip if no shadow root
  if (!element.shadowRoot) return;

  // Define window with our global state props
  interface FluidWindow extends Window {
    __FLUID_STYLE_TEXT?: string;
    __FLUID_STYLE_URL?: string;
    __FLUID_SHADOW_STYLESHEET?: CSSStyleSheet;
    __FLUID_SHADOW_STYLESHEET_LOADING?: boolean;
  }

  const fluidWindow = window as unknown as FluidWindow;

  // Check if browser supports Constructable StyleSheets with more robust detection
  const supportsConstructableStyleSheets = (() => {
    try {
      // Check for basic API support
      if (
        !("adoptedStyleSheets" in ShadowRoot.prototype) ||
        typeof CSSStyleSheet === "undefined"
      ) {
        return false;
      }

      // Test if we can actually create a CSSStyleSheet
      const testSheet = new CSSStyleSheet();
      if (typeof testSheet.replaceSync !== "function") {
        return false;
      }

      // Test if we can actually use adoptedStyleSheets
      testSheet.replaceSync("/* test */");
      const testDiv = document.createElement("div");
      const testShadow = testDiv.attachShadow({ mode: "open" });
      testShadow.adoptedStyleSheets = [testSheet];

      return true;
    } catch {
      return false;
    }
  })();

  if (!supportsConstructableStyleSheets) {
    console.warn(
      "Browser does not support Constructable StyleSheets. Styles may not be applied correctly.",
    );
    return;
  }

  // Apply styles using modern API
  if (fluidWindow.__FLUID_SHADOW_STYLESHEET) {
    // Use cached stylesheet if available
    const existingSheets = Array.from(element.shadowRoot.adoptedStyleSheets);
    if (
      !existingSheets.some(
        (sheet) => sheet === fluidWindow.__FLUID_SHADOW_STYLESHEET,
      )
    ) {
      try {
        element.shadowRoot.adoptedStyleSheets = [
          ...existingSheets,
          fluidWindow.__FLUID_SHADOW_STYLESHEET,
        ];
      } catch (error) {
        console.error("Error applying cached stylesheet:", error);
      }
    }
  } else {
    // No cached stylesheet yet, check if we have CSS text
    if (fluidWindow.__FLUID_STYLE_TEXT) {
      try {
        // Create a new stylesheet
        const stylesheet = new CSSStyleSheet();
        stylesheet.replaceSync(fluidWindow.__FLUID_STYLE_TEXT);

        // Cache for future use
        fluidWindow.__FLUID_SHADOW_STYLESHEET = stylesheet;

        // Apply to shadow root
        element.shadowRoot.adoptedStyleSheets = [
          ...Array.from(element.shadowRoot.adoptedStyleSheets),
          stylesheet,
        ];
      } catch (error) {
        console.error("Error applying constructable stylesheet:", error);
      }
    } else if (
      fluidWindow.__FLUID_STYLE_URL &&
      !fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING
    ) {
      // Mark as loading to prevent multiple fetches
      fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = true;

      // Fetch the styles and apply them
      fetch(fluidWindow.__FLUID_STYLE_URL)
        .then((response) => response.text())
        .then((cssText) => {
          // Cache the CSS text
          fluidWindow.__FLUID_STYLE_TEXT = cssText;

          // Don't proceed if element no longer has a shadow root
          if (!element.shadowRoot) return;

          try {
            // Create stylesheet
            const stylesheet = new CSSStyleSheet();
            stylesheet.replaceSync(cssText);

            // Cache for future use
            fluidWindow.__FLUID_SHADOW_STYLESHEET = stylesheet;
            fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = false;

            // Apply to shadow root
            element.shadowRoot.adoptedStyleSheets = [
              ...Array.from(element.shadowRoot.adoptedStyleSheets),
              stylesheet,
            ];
          } catch (error) {
            console.error("Error creating stylesheet:", error);
            fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = false;
          }
        })
        .catch((error) => {
          console.error("Error fetching styles:", error);
          fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = false;
        });
    }
  }
}
